import { operationIds } from "@gen/app-access-config";
import {
  type ClientResponse,
  CoreQuery,
  CreateClientWithIdBody,
  type ICreateClientWithIdBody,
} from "@gen/app-query";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { typeid } from "typeid-js";
import type * as z from "zod";

import { HeadingContainer } from "~/components/heading_container";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import { TextInput } from "~/components/text_input";
import { useAuthOperations } from "~/contexts/auth_query";
import {
  clientRedirectUriSchema,
  createClientFormSchema,
} from "~/entities/create_client";
import { config } from "~root/config";

import {
  RedirectURIsEditorWithIds,
  type RedirectUriWithId,
} from "../RedirectURIsEditor";
import { CreateClientImportDialog } from "./CreateClientImportDialog";

type FormValues = z.infer<typeof createClientFormSchema>;

export function CreateClient() {
  const navigate = useNavigate({ from: "/admin/clients/create" });
  const { mutate, isPending: isCreateClientUpdating } =
    CoreQuery.useAdmin_createClientWithIdMutation({
      onSuccess(data: ClientResponse) {
        navigate({
          to: "/admin/clients/" + data.id,
        });
      },
    });
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    formState: { isValid },
  } = useForm<FormValues>({
    mode: "onTouched",
    resolver: zodResolver(createClientFormSchema),
    defaultValues: {
      base_url: "",
      contact_email: "",
      id: typeid(config.clientIdPrefix).toString(),
      name: "",
      redirect_uris: [] as RedirectUriWithId[],
    },
  });

  // RedirectURIs are updated via the updateClient operation.
  const { canExecuteAdminOperation } = useAuthOperations();
  const canUpdateRedirectURIs = canExecuteAdminOperation(
    operationIds["core_admin.updateClient"],
  );

  const [isImportDialogOpen, setImportDialogOpen] = useState(false);

  const onImportData = (partial: Partial<ICreateClientWithIdBody>) => {
    (Object.keys(partial) as Array<keyof ICreateClientWithIdBody>).forEach(
      (key) => {
        const incoming = partial[key];
        if (incoming === undefined || incoming === null) {
          return;
        }

        if (key === "redirect_uris" && Array.isArray(incoming)) {
          const redirectUrisWithIds: RedirectUriWithId[] = incoming.map(
            (uri) => ({
              id: crypto.randomUUID(),
              uri: uri,
            }),
          );
          setValue("redirect_uris", redirectUrisWithIds, {
            shouldDirty: true,
            shouldValidate: true,
          });
        } else if (key !== "redirect_uris") {
          const formKey = key as keyof Omit<FormValues, "redirect_uris">;
          const current = getValues(formKey);
          if (current !== incoming) {
            setValue(formKey, incoming as FormValues[typeof formKey], {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        }
      },
    );

    trigger();
  };

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    const apiData: ICreateClientWithIdBody = {
      ...data,
      redirect_uris: data.redirect_uris
        .map((r) => r.uri.trim())
        .filter((uri) => uri !== ""),
    };
    mutate(CreateClientWithIdBody.fromJS(apiData));
  };

  const handleImportClick = () => {
    setImportDialogOpen(true);
  };

  const renderField = (
    name: keyof FormValues,
    label: string,
    placeholder: string,
    multiline: boolean = false,
  ) => (
    <Controller
      key={name}
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TextInput
          {...field}
          data-testid={`client-${name}-input`}
          placeholder={placeholder}
          label={label}
          clearable
          multiline={multiline}
          onClear={() => field.onChange("")}
          onBlur={(e) => {
            field.onBlur();
            field.onChange(e.target.value.trim());
          }}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          sx={{
            flexGrow: 1,
          }}
        />
      )}
    />
  );

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={5}>
          <Stack
            spacing={5}
            sx={{
              backgroundColor: "primary._states.hover",
              p: 5,
              borderRadius: 2,
            }}
          >
            <HeadingContainer
              title={
                <>
                  <PublicSvgIcon name="code_blocks" /> Client Settings
                </>
              }
              variant="h5"
              borderType="solid"
            >
              <IconButton
                disabled={isCreateClientUpdating}
                aria-label="Import JSON configuration"
                onClick={handleImportClick}
                variant="withBackground"
                data-testid="import-client-button"
              >
                <PublicSvgIcon name="place_item" />
              </IconButton>
            </HeadingContainer>
            <Stack direction="row" justifyContent="space-between" spacing={5}>
              {renderField("id", "Client ID", "Client ID", true)}
              {renderField("name", "Client Name", "Client Name")}
              {renderField("contact_email", "Contact Email", "Contact Email")}
              {renderField("base_url", "Base URL", "Base URL")}
            </Stack>
          </Stack>
          <Controller
            name="redirect_uris"
            control={control}
            render={({ field, fieldState }) => (
              <RedirectURIsEditorWithIds
                value={field.value}
                onChange={field.onChange}
                canDelete={canUpdateRedirectURIs}
                zodSchema={clientRedirectUriSchema}
                error={fieldState.error?.message}
              />
            )}
          />

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              type="submit"
              loading={isCreateClientUpdating}
              disabled={!isValid}
              data-testid="create-client-button"
            >
              Create Client
            </Button>
          </Stack>
        </Stack>
      </form>

      <CreateClientImportDialog
        isOpen={isImportDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={onImportData}
      />
    </>
  );
}
